import { defineStore } from "pinia";
import agentService from "@/api/agentService";

export const useAgentStore = defineStore("agent", {
  state: () => ({
    agents: [],
    currentAgent: null,
    totalAgents: 0,
    currentPage: 1,
    pageSize: 10,
    isLoading: false,
  }),
  actions: {
    // Fetch all agents with pagination
    async fetchAgents(page = 1, size = 10, projectIds = []) {
      this.isLoading = true;
      try {
        const params = { page, size };
        if (projectIds.length > 0) {
          params.project_ids = projectIds;
        }
        const { data } = await agentService.getAgents(params);
        this.agents = data.items;
        this.totalAgents = data.total;
        this.currentPage = page;
        this.pageSize = size;
      } catch (error) {
        console.error("Error fetching agents:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Fetch a single agent by ID
    async fetchAgent(agentId) {
      this.isLoading = true;
      try {
        const { data } = await agentService.getAgentById(agentId);
        this.currentAgent = data || null;
        return this.currentAgent;
      } catch (error) {
        console.error("Error fetching agent by ID:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Create a new agent
    async createAgent(agentData) {
      this.isLoading = true;
      try {
        const { data } = await agentService.createAgent(agentData);
        this.agents.push(data);
      } catch (error) {
        console.error("Error creating agent:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Update an existing agent
    async updateAgent(agentId, agentData) {
      this.isLoading = true;
      try {
        const { data } = await agentService.updateAgent(agentId, agentData);
        const index = this.agents.findIndex((agent) => agent.id === agentId);
        if (index !== -1) {
          this.agents.splice(index, 1, data);
        }
      } catch (error) {
        console.error("Error updating agent:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Delete an agent
    async deleteAgent(agentId) {
      this.isLoading = true;
      try {
        await agentService.deleteAgent(agentId);
        this.agents = this.agents.filter((agent) => agent.id !== agentId);
      } catch (error) {
        console.error("Error deleting agent:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Search agents
    async searchAgents(query, page = 1, size = 10, projectIds = []) {
      this.isLoading = true;
      try {
        if (!query || query.length < 1) {
          throw new Error("Search query must be at least 1 character long.");
        }
        const params = { query, page, size };
        if (projectIds.length > 0) {
          params.project_ids = projectIds;
        }
        const { data } = await agentService.searchAgents(params);
        this.agents = data.items;
        this.totalAgents = data.total;
        this.currentPage = page;
        this.pageSize = size;
      } catch (error) {
        console.error("Error searching agents:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // NEW: Fetch available models
    async fetchAvailableModels() {
      try {
        const response = await agentService.getAvailableModels();
        return response;
      } catch (error) {
        console.error("Error fetching available models:", error);
        throw error;
      }
    },
  },
});
